import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { ClusterService, ResolvedLanguage } from '../cluster.service';

/**
 * Presentational language switcher — no service dependencies.
 * Accepts languages dynamically via input, emits selection via output.
 * Can be reused in any app that provides its own language data.
 */
@Component({
    selector: 'risr-language-switcher',
    imports: [MatButtonModule, MatMenuModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
        .lang-icon { width: 20px; height: 20px; fill: currentColor; vertical-align: middle; }
        .lang-menu-icon { width: 18px; height: 18px; fill: currentColor; vertical-align: middle; margin-right: 8px; }
    `],
    template: `
        @if (languages().length > 0) {
            <button mat-icon-button
                    [matMenuTriggerFor]="languageMenu"
                    i18n-aria-label
                    aria-label="Languages menu">
                <svg class="lang-icon" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M448 164C459 164 468 172.1 468 184V188H528C539 188 548 196.1 548 208C548 219 539 228 528 228H526L524.4 232.5C515.5 256.1 501.9 279.1 484.7 297.9C485.6 298.4 486.5 298.1 487.4 299.5L506.3 310.8C515.8 316.5 518.8 328.8 513.1 338.3C507.5 347.8 495.2 350.8 485.7 345.1L466.8 333.8C462.4 331.1 457.1 328.3 453.7 325.3C443.2 332.8 431.8 339.3 419.8 344.7L416.1 346.3C406 350.8 394.2 346.2 389.7 336.1C385.2 326 389.8 314.2 399.9 309.7L403.5 308.1C409.9 305.2 416.1 301.1 422 298.3L409.9 286.1C402 278.3 402 265.7 409.9 257.9C417.7 250 430.3 250 438.1 257.9L452.7 272.4L453.3 272.1C465.7 259.9 475.8 244.7 483.1 227.1H376C364.1 227.1 356 219 356 207.1C356 196.1 364.1 187.1 376 187.1H428V183.1C428 172.1 436.1 163.1 448 163.1L448 164zM160 233.2L179 276H140.1L160 233.2zM0 128C0 92.65 28.65 64 64 64H576C611.3 64 640 92.65 640 128V384C640 419.3 611.3 448 576 448H64C28.65 448 0 419.3 0 384V128zM320 384H576V128H320V384zM178.3 175.9C175.1 168.7 167.9 164 160 164C152.1 164 144.9 168.7 141.7 175.9L77.72 319.9C73.24 329.1 77.78 341.8 87.88 346.3C97.97 350.8 109.8 346.2 114.3 336.1L123.2 315.1H196.8L205.7 336.1C210.2 346.2 222 350.8 232.1 346.3C242.2 341.8 246.8 329.1 242.3 319.9L178.3 175.9z"/>
                </svg>
            </button>
            <mat-menu #languageMenu="matMenu">
                @for (language of languages(); track language.language_code) {
                    <button mat-menu-item (click)="languageSelected.emit(language)">
                        <svg class="lang-menu-icon" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M448 164C459 164 468 172.1 468 184V188H528C539 188 548 196.1 548 208C548 219 539 228 528 228H526L524.4 232.5C515.5 256.1 501.9 279.1 484.7 297.9C485.6 298.4 486.5 298.1 487.4 299.5L506.3 310.8C515.8 316.5 518.8 328.8 513.1 338.3C507.5 347.8 495.2 350.8 485.7 345.1L466.8 333.8C462.4 331.1 457.1 328.3 453.7 325.3C443.2 332.8 431.8 339.3 419.8 344.7L416.1 346.3C406 350.8 394.2 346.2 389.7 336.1C385.2 326 389.8 314.2 399.9 309.7L403.5 308.1C409.9 305.2 416.1 301.1 422 298.3L409.9 286.1C402 278.3 402 265.7 409.9 257.9C417.7 250 430.3 250 438.1 257.9L452.7 272.4L453.3 272.1C465.7 259.9 475.8 244.7 483.1 227.1H376C364.1 227.1 356 219 356 207.1C356 196.1 364.1 187.1 376 187.1H428V183.1C428 172.1 436.1 163.1 448 163.1L448 164zM160 233.2L179 276H140.1L160 233.2zM0 128C0 92.65 28.65 64 64 64H576C611.3 64 640 92.65 640 128V384C640 419.3 611.3 448 576 448H64C28.65 448 0 419.3 0 384V128zM320 384H576V128H320V384zM178.3 175.9C175.1 168.7 167.9 164 160 164C152.1 164 144.9 168.7 141.7 175.9L77.72 319.9C73.24 329.1 77.78 341.8 87.88 346.3C97.97 350.8 109.8 346.2 114.3 336.1L123.2 315.1H196.8L205.7 336.1C210.2 346.2 222 350.8 232.1 346.3C242.2 341.8 246.8 329.1 242.3 319.9L178.3 175.9z"/>
                        </svg>
                        <span>{{ language.language_name }}</span>
                    </button>
                }
            </mat-menu>
        }
    `,
})
export class LanguageSwitcherComponent {
    languages = input.required<ResolvedLanguage[]>();
    languageSelected = output<ResolvedLanguage>();
}

/**
 * Smart container — reads available languages from ClusterService and
 * handles navigation. Use this in Assembly and Kaizen.
 */
@Component({
    selector: 'risr-language-switcher-container',
    imports: [LanguageSwitcherComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <risr-language-switcher
            [languages]="languages"
            (languageSelected)="setLanguage($event)">
        </risr-language-switcher>
    `,
})
export class LanguageSwitcherContainerComponent {
    private cluster = inject(ClusterService);
    private router = inject(Router);

    languages = this.cluster.getOtherLanguages();

    setLanguage(language: ResolvedLanguage) {
        const url = `${language.base_href}${this.router.url}`;
        location.href = url;
    }
}
